/**
 * Local Authentication Service for development
 * Uses backend endpoints instead of Firebase
 */

const API_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL || 'http://localhost:8080';

export interface LocalAuthResponse {
  idToken: string;
  email: string;
  uid: string;
  message: string;
}

export class EmailRedanRegistreradFel extends Error {
  constructor(public readonly email: string, message: string) {
    super(message);
    this.name = 'EmailRedanRegistreradFel';
  }
}

export async function registerLocal(email: string, password: string): Promise<LocalAuthResponse> {
  const response = await fetch(`${API_URL}/api/v1/auth/local/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const msg = data?.message || 'Registreringen misslyckades.';
    if (response.status === 400 && msg.toLowerCase().includes('already registered')) {
      throw new EmailRedanRegistreradFel(email, msg);
    }
    throw new Error(msg);
  }

  return data as LocalAuthResponse;
}

/**
 * Adressen saknar lokalt konto. Egen feltyp så att anroparen kan skicka vidare till
 * registreringen i stället för att visa ett felmeddelande — `message` duger inte som signal,
 * texten kommer från backend och kan skrivas om.
 */
export class LokaltKontoSaknasFel extends Error {
  constructor(public readonly email: string, message: string) {
    super(message);
    this.name = 'LokaltKontoSaknasFel';
  }
}

export async function loginLocal(email: string, password: string): Promise<LocalAuthResponse> {
  const response = await fetch(`${API_URL}/api/v1/auth/local/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json().catch(() => null);

  if (response.status === 404) {
    throw new LokaltKontoSaknasFel(email, data?.message || 'User not found');
  }

  if (!response.ok) {
    throw new Error(data?.message || `Login failed: ${response.statusText}`);
  }

  return data as LocalAuthResponse;
}

/**
 * Återställer åtkomsten till ett lokalt konto när lösenordet är bortglömt. Backend sparar inget
 * lösenord i den lokala profilen, så återställningen delar ut en ny token — du blir inloggad igen.
 * Felmeddelandet läses ur svarskroppen; `statusText` är tomt över HTTP/2.
 */
export async function återställLokalÅtkomst(email: string): Promise<LocalAuthResponse> {
  const response = await fetch(`${API_URL}/api/v1/auth/local/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || 'Återställningen misslyckades.');
  }

  return data as LocalAuthResponse;
}
